import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';

const VisaServices: React.FC = () => {
  const [activeVisa, setActiveVisa] = useState<number | null>(null);

  const visaTypes = [
    {
      title: "Student Visa",
      icon: "🎓",
      description: "For international students pursuing education in Japan",
      requirements: [
        "School acceptance letter",
        "Financial documents",
        "Academic transcripts",
        "Passport and photos"
      ],
      color: "from-blue-600 to-cyan-500",
      duration: "Up to 4 years 3 months",
      processingTime: "2-3 months"
    },
    // {
    //   title: "Work Visa",
    //   icon: "💼",
    //   description: "For professionals with job offers in Japan",
    //   requirements: [
    //     "Employment contract",
    //     "Company documents",
    //     "Educational certificates",
    //     "Work experience proof"
    //   ],
    //   color: "from-purple-600 to-pink-500",
    //   duration: "1-5 years",
    //   processingTime: "1-2 months"
    // },
    // {
    //   title: "Business Visa",
    //   icon: "🏢",
    //   description: "For business activities and investments in Japan",
    //   requirements: [
    //     "Business plan",
    //     "Company registration",
    //     "Financial statements",
    //     "Investment proof"
    //   ],
    //   color: "from-orange-500 to-red-500",
    //   duration: "3 months - 5 years",
    //   processingTime: "2-4 weeks"
    // }
  ];

  return (
    <div className="min-h-screen pt-24 relative overflow-hidden bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900">
      {/* Animated background */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          className="absolute inset-0 bg-gradient-to-br from-blue-500/10 via-purple-500/10 to-pink-500/10"
          animate={{
            opacity: [0.5, 0.8, 0.5]
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        {Array.from({ length: 20 }).map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 rounded-full"
            style={{
              background: `rgba(255, 255, 255, ${0.1 + Math.random() * 0.3})`,
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`
            }}
            animate={{
              y: [0, Math.random() * 300 - 150],
              x: [0, Math.random() * 300 - 150],
              opacity: [0, 1, 0]
            }}
            transition={{
              duration: 3 + Math.random() * 2,
              repeat: Infinity,
              ease: "linear",
              delay: Math.random() * 2
            }}
          />
        ))}
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          className="text-center mb-20"
        >
          <motion.h1 
            className="text-7xl md:text-8xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 mb-8"
            animate={{
              backgroundPosition: ['0% 50%', '100% 50%', '0% 50%']
            }}
            transition={{
              duration: 10,
              repeat: Infinity,
              ease: "linear"
            }}
            style={{ backgroundSize: '200% auto' }}
          >
            Visa Services
          </motion.h1>
          <motion.p 
            className="text-2xl text-gray-300 max-w-3xl mx-auto leading-relaxed"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            Expert guidance through every step of your Japanese visa application process
          </motion.p>
        </motion.div>

        {/* Main Content */}
        <div className="relative">
          {/* Timeline line */}
          <motion.div
            className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-blue-500/50 via-purple-500/50 to-pink-500/50 hidden md:block"
            initial={{ scaleY: 0 }}
            animate={{ scaleY: 1 }}
            transition={{ duration: 1.5, delay: 0.5 }}
          />

          {/* Visa Types */}
          <div className="space-y-16 md:space-y-32">
            {visaTypes.map((visa, index) => (
              <motion.div
                key={visa.title}
                className={`relative flex flex-col md:flex-row items-center ${
                  index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                }`}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
              >
                {/* Content Side */}
                <div className={`w-full md:w-1/2 ${index % 2 === 0 ? 'md:pr-12' : 'md:pl-12'}`}>
                  <motion.div
                    className={`relative p-6 md:p-8 rounded-3xl bg-gradient-to-br ${visa.color} backdrop-blur-lg`}
                    whileHover={{ scale: 1.02 }}
                    transition={{ duration: 0.3 }}
                  >
                    <motion.div
                      className="absolute inset-0 bg-white/10 rounded-3xl"
                      animate={{
                        opacity: [0.5, 0.8, 0.5]
                      }}
                      transition={{
                        duration: 3,
                        repeat: Infinity,
                        ease: "easeInOut"
                      }}
                    />
                    <div className="relative z-10">
                      <motion.div 
                        className="text-5xl md:text-7xl mb-4 md:mb-6"
                        whileHover={{ scale: 1.1, rotate: 10 }}
                        transition={{ duration: 0.3 }}
                      >
                        {visa.icon}
                      </motion.div>
                      <h3 className="text-2xl md:text-3xl font-bold text-white mb-3 md:mb-4">{visa.title}</h3>
                      <p className="text-white/90 text-base md:text-lg mb-6 md:mb-8">{visa.description}</p>
                      <div className="flex flex-col md:flex-row justify-between text-white/80 text-sm mb-6 md:mb-8 space-y-2 md:space-y-0">
                        <span>Duration: {visa.duration}</span>
                        <span>Processing: {visa.processingTime}</span>
                      </div>
                      <motion.button
                        className="text-white font-semibold text-base md:text-lg flex items-center group"
                        onClick={() => setActiveVisa(activeVisa === index ? null : index)}
                        whileHover={{ x: 5 }}
                      >
                        View Requirements
                        <motion.svg
                          className="w-5 h-5 md:w-6 md:h-6 ml-2"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          animate={{
                            x: activeVisa === index ? [0, 5, 0] : 0
                          }}
                          transition={{
                            duration: 1,
                            repeat: activeVisa === index ? Infinity : 0
                          }}
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                        </motion.svg>
                      </motion.button>
                    </div>
                  </motion.div>
                </div>

                {/* Timeline dot */}
                <div className="absolute left-1/2 transform -translate-x-1/2 hidden md:block">
                  <motion.div
                    className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-500 to-purple-500"
                    animate={{
                      scale: activeVisa === index ? [1, 1.2, 1] : 1,
                      opacity: activeVisa === index ? [1, 0.8, 1] : 1
                    }}
                    transition={{
                      duration: 2,
                      repeat: activeVisa === index ? Infinity : 0
                    }}
                  />
                </div>

                {/* Requirements Panel */}
                <AnimatePresence>
                  {activeVisa === index && (
                    <motion.div
                      initial={{ opacity: 0, x: index % 2 === 0 ? 50 : -50 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: index % 2 === 0 ? 50 : -50 }}
                      transition={{ duration: 0.3 }}
                      className={`absolute md:relative top-0 ${
                        index % 2 === 0 
                          ? 'md:left-[60%] md:ml-4' 
                          : 'md:right-[60%] md:mr-4'
                      } w-full md:w-96 bg-white/10 backdrop-blur-lg rounded-2xl p-4 md:p-6 z-50 mt-4 md:mt-0`}
                    >
                      <h4 className="text-lg md:text-xl font-semibold text-white mb-4 md:mb-6">Requirements</h4>
                      <ul className="space-y-3 md:space-y-4">
                        {visa.requirements.map((req, i) => (
                          <motion.li
                            key={i}
                            className="flex items-center text-white/90 text-sm md:text-base"
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: i * 0.1 }}
                          >
                            <motion.div
                              className="w-2.5 h-2.5 md:w-3 md:h-3 rounded-full bg-white/20 mr-2 md:mr-3 flex items-center justify-center"
                              animate={{
                                opacity: [0.5, 1, 0.5]
                              }}
                              transition={{
                                duration: 2,
                                repeat: Infinity,
                                delay: i * 0.2
                              }}
                            >
                              <div className="w-1.5 h-1.5 md:w-1.5 md:h-1.5 rounded-full bg-white" />
                            </motion.div>
                            {req}
                          </motion.li>
                        ))}
                      </ul>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Call to Action */}
        <motion.div
          className="text-center mt-32"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
        >
          <Link to="/contact">
            <motion.button
              className="px-12 py-6 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 text-white text-xl font-bold rounded-full relative overflow-hidden group mb-4"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <motion.span
                className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent"
                animate={{
                  x: ['-100%', '100%']
                }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  ease: "linear"
                }}
              />
              Start Your Visa Application
            </motion.button>
          </Link>
        </motion.div>
      </div>

      {/* Mobile Responsive Adjustments */}
      <style>{`
        @media (max-width: 768px) {
          .visa-content {
            flex-direction: column !important;
          }
          .visa-content > div {
            width: 100% !important;
            padding: 0 !important;
          }
          .requirements-panel {
            position: fixed !important;
            top: 50% !important;
            left: 50% !important;
            transform: translate(-50%, -50%) !important;
            width: 90% !important;
            max-width: 400px !important;
            margin: 0 !important;
            z-index: 100 !important;
          }
        }
      `}</style>
    </div>
  );
};

export default VisaServices; 